'use client'

import Image from 'next/image'
import { useOptimistic } from 'react'

import { Alert } from '@src/app/[country]/[lang]/checkout/modules/Alert'
import { Guarantee } from '@src/app/[country]/[lang]/checkout/modules/Guarantee'
import { LineItemPrice } from '@src/app/[country]/[lang]/checkout/modules/LineItemPrice'
import { Button } from '@src/components/Button/Button'
import { CartPageContentDefaults } from '@src/components/CartPageContent/CartPageContent.defaults'
import { type CartPageContentProps } from '@src/components/CartPageContent/CartPageContent.props'
import { EmptyCartPageContent } from '@src/components/CartPageContent/EmptyCartPageContent'
import { LoadingCartPageContent } from '@src/components/CartPageContent/LoadingCartPageContent'
import { LineItemSettings } from '@src/components/CartPageContent/components/LineItemSettings'
import { Container } from '@src/components/Container/Container'
import { DiscountList } from '@src/components/DiscountList/DiscountList'
import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink'
import { Spinner } from '@src/components/Spinner/Spinner'
import { useCart } from '@src/hooks/useCart'
import { useEffectOnce } from '@src/hooks/useEffectOnce'
import { useErrors } from '@src/hooks/useErrors'
import { useLoadings } from '@src/hooks/useLoadings'
import { useLocale } from '@src/i18n/LocaleProvider'
import { useTrackCartFlyoutOpen } from '@src/utils/analytics.util'
import { useBreakpoints } from '@src/utils/breakpoints.util'
import { formatCartAmount, parseGuarantees } from '@src/utils/cart.util'
import { ErrorCodes } from '@src/utils/constants.util'
import { getLineItemInfo } from '@src/utils/getLineItemInfo'
import { PRODUCT_TYPE_META } from '@src/utils/productMeta.util'
import { cn } from '@src/utils/tailwind'

export const CartPageContent = (props: CartPageContentProps = CartPageContentDefaults) => {
  const { cart, isLoading: isLoadingCart } = useCart()
  const { t, locale } = useLocale('common')
  const { t: tcp } = useLocale('cart-page')
  const { loadings } = useLoadings()
  const { setError, ...errors } = useErrors()
  const { isMobile } = useBreakpoints()
  const allGuarantees = parseGuarantees(cart ?? null, locale.code)
  const trackCartFlyoutOpen = useTrackCartFlyoutOpen()

  const lineItems = cart?.lineItems ?? []

  const [optimisticQuantities, setOptimisticQuantities] = useOptimistic(
    lineItems.map((lineItem) => ({
      sku: lineItem.variant.sku,
      quantity: lineItem.quantity ?? 1,
    }))
  )

  const copy = props.copy
  const continueLink = props.continueLink ?? '/checkout-start'
  const isEmptyCart = !isLoadingCart && lineItems.length === 0
  const isQuantityError = errors?.code === ErrorCodes.InvalidQuantity
  const mobileDrawerView = props.isDrawer ?? isMobile
  const mobileNotDrawerView = !props.isDrawer && isMobile
  const notMobileOrDrawerView = !props.isDrawer && !isMobile
  const drawerOrNotMobileView = props.isDrawer ?? !isMobile

  const isAnyLoading =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    loadings.isRemovingLineItems || loadings.isAddingLineItems || loadings.isChangingLineItems

  const allOptimisticQuantities = optimisticQuantities.reduce((acc, q) => acc + q.quantity, 0)

  const optimisticSubtotal = lineItems.reduce((acc, item) => {
    const quantity = optimisticQuantities.find((q) => q.sku === item.variant.sku)?.quantity ?? 1
    const info = getLineItemInfo({
      lineItem: item,
      lang: locale.lang,
      country: locale.countryCodeLowercase,
      quantity,
      t,
    })

    return acc + (info?.totalPriceDiscountedCents ?? info?.totalPriceCents ?? 0)
  }, 0)

  const optimisticSubtotalFormatted = formatCartAmount(optimisticSubtotal, t)
  const isRemovingOptimisticQuantity = optimisticQuantities.some((q) => q.quantity === 0)

  useEffectOnce(() => {
    if (props.isDrawer) {
      trackCartFlyoutOpen(cart)
    }
  }, [props.isDrawer])

  if (isLoadingCart) return <LoadingCartPageContent />
  if (isEmptyCart) return <EmptyCartPageContent {...props} />

  const renderSubtotalHeading = () => {
    return (
      <div>
        {`${copy?.subtotal ?? tcp('subtotal')}${
          allOptimisticQuantities > 0 ? ` (${allOptimisticQuantities})` : ''
        }`}
      </div>
    )
  }

  const renderSubtotal = () => {
    return (
      <div>
        {isAnyLoading || isRemovingOptimisticQuantity ? (
          <Spinner size="smaller" />
        ) : (
          optimisticSubtotalFormatted
        )}
      </div>
    )
  }

  const renderCartSummary = () => (
    <div
      className={cn({
        'fixed bottom-0 z-20 w-full max-w-md bg-bodi-black p-8 text-bodi-white': mobileDrawerView,
      })}
    >
      <div className="mb-2 flex items-center justify-between text-body-xl font-medium">
        {renderSubtotalHeading()}
        {renderSubtotal()}
      </div>
      <p className="text-body-xs text-bodi-gray-medium">
        {copy?.subtotalDisclaimer ?? tcp('subtotal-disclaimer')}
      </p>

      <LocalizedLink href={continueLink}>
        <Button
          data-testid="continueBtn"
          size="large"
          className="mt-6 w-full bg-bodi-green-bright-500 text-bodi-black hover:bg-bodi-gray"
        >
          {copy?.continue ?? tcp('continue-to-checkout')}
        </Button>
      </LocalizedLink>
    </div>
  )

  const renderColumnLeft = () => {
    return (
      <div className="col-span-2">
        {!props.isDrawer && (
          <h1 className="text-heading-display-1 mb-4 lg:mb-8 lg:h-[52px]">
            {copy?.yourCart ?? tcp('your-cart')}
          </h1>
        )}
        {isQuantityError && (
          <Alert
            type="error"
            className="mb-4"
            message={t('error-quantity', {
              quantity: PRODUCT_TYPE_META.nutrition.maxQuantity.toString(),
            })}
          />
        )}
        <ul className="@container flex flex-col gap-2">
          {lineItems?.map((lineItem) => {
            const quantity =
              optimisticQuantities.find((q) => q.sku === lineItem.variant.sku)?.quantity ??
              undefined
            const info = getLineItemInfo({
              lineItem,
              lang: locale.lang,
              country: locale.countryCodeLowercase,
              quantity,
              t,
            })

            return (
              <div
                key={lineItem.id}
                className="@container flex flex-col gap-6 rounded-3xl bg-bodi-white p-6 @lg:flex-row @lg:p-8"
              >
                <div className="flex w-full items-start gap-2 @lg:gap-6">
                  <div className="@container flex h-[68px] w-[100px] shrink-0 items-center justify-center rounded-lg bg-bodi-gray-pearl @lg:h-[170px] @lg:w-[250px]">
                    <Image
                      alt={info?.img?.alt ?? ''}
                      src={info?.img?.src ?? ''}
                      width={250}
                      height={170}
                      className={cn('h-[66px] w-[64px] bg-bodi-gray-pearl object-cover', {
                        'lg:h-[65px] lg:w-[100px]': mobileDrawerView,
                        'lg:h-[170px] lg:w-[164px]': notMobileOrDrawerView,
                      })}
                    />
                  </div>

                  <div>
                    <DiscountList discountNames={info?.discountNames} variant="cart" />
                    <h3 className="mb-1 text-body-xl font-medium">{info?.name}</h3>
                    <div>
                      {info?.computedDetails.map((label) => (
                        <p key={label} className="text-bodi-gray-medium">
                          {label}
                        </p>
                      ))}
                      {info?.marketingDetails && (
                        <>
                          <div className="my-4 h-px bg-bodi-gray-light" />
                          <p
                            className="prose text-body-sm font-semibold"
                            dangerouslySetInnerHTML={{
                              __html: info?.marketingDetails,
                            }}
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex justify-between gap-6 @lg:ml-auto @lg:flex-col @lg:items-end @lg:justify-normal">
                  <div className="order-last flex items-center gap-2 font-medium @lg:order-first">
                    {(quantity ?? 1) > 0 ? (
                      <LineItemPrice
                        basePriceCents={info?.totalPriceCents}
                        discountedPriceCents={info?.totalPriceDiscountedCents}
                      />
                    ) : (
                      <Spinner size="smaller" />
                    )}
                  </div>
                  <LineItemSettings
                    lineItem={lineItem}
                    optimisticQuantity={quantity ?? lineItem.quantity ?? 1}
                    setOptimisticQuantity={(quantity: number) =>
                      setOptimisticQuantities((prev) =>
                        prev.map((q) => (q.sku === lineItem.variant.sku ? { ...q, quantity } : q))
                      )
                    }
                  />
                </div>
              </div>
            )
          })}
        </ul>
      </div>
    )
  }

  const renderColumnRight = () => {
    return (
      <div className="@container col-span-1">
        {notMobileOrDrawerView && (
          <>
            <h3 className="text-heading-display-3 mb-4 flex items-end @lg:mb-8 @lg:h-[52px]">
              {copy?.orderSummary ?? tcp('order-summary')}
            </h3>

            <div>
              <div className="mb-2 flex items-center justify-between text-body-xl font-medium">
                {renderSubtotalHeading()}
                {renderSubtotal()}
              </div>
              <p className="text-body-xs text-bodi-gray-medium">
                {copy?.subtotalDisclaimer ?? tcp('subtotal-disclaimer')}
              </p>

              <LocalizedLink href={continueLink}>
                <Button data-testid="continueBtn" size="large" className="mt-8 w-full">
                  {copy?.continue ?? tcp('continue-to-checkout')}
                </Button>
              </LocalizedLink>
            </div>
          </>
        )}
        {allGuarantees.map((guarantee) => (
          <Guarantee
            className="text-body-md font-medium text-bodi-black-text @lg:text-body-md"
            key={guarantee.id}
            content={guarantee.content}
            icon={guarantee.icon}
          />
        ))}
      </div>
    )
  }

  return (
    <div
      className={cn('bg-bodi-gray-pearl pb-24', {
        'py-12': drawerOrNotMobileView,
        'lg:py-20 lg:pb-48': notMobileOrDrawerView,
        'pb-0': mobileNotDrawerView,
      })}
    >
      <Container
        includeContentPadding={false}
        className={cn('@container', {
          'px-8 ': mobileDrawerView,
          'pt-8': mobileNotDrawerView,
        })}
        fill={mobileDrawerView}
      >
        <div
          className={cn('flex flex-col text-bodi-black-text @lg:grid @lg:grid-cols-3', {
            'gap-2': mobileDrawerView,
            'gap-10': notMobileOrDrawerView,
          })}
        >
          {renderColumnLeft()}
          {renderColumnRight()}
        </div>
      </Container>

      {/* Add padding at the bottom when in drawer mode to account for the fixed summary */}
      {props.isDrawer && <div className="h-40"></div>}

      {/* Render the cart summary outside the content flow when in drawer mode */}
      {mobileDrawerView && renderCartSummary()}
    </div>
  )
}
